import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Seo from "@/components/site/Seo";
import { PawPrint, Camera, CheckCircle, ArrowLeft } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const labelCls = "overline block mb-2";
const inputCls =
  "w-full bg-white border border-emerald/15 rounded-xl px-4 py-3 text-sm text-emerald placeholder:text-emerald-soft/50 focus:outline-none focus:ring-1 focus:ring-sunset";

export default function PetPhotoUpload() {
  const [file, setFile] = useState(null);
  const [petNames, setPetNames] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastInitial, setLastInitial] = useState("");
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [rights, setRights] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [error, setError] = useState("");

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file || !petNames.trim() || !firstName.trim() || !lastInitial.trim() || !comment.trim()) {
      setError("Please complete every field and choose a photo.");
      return;
    }
    if (!rights) {
      setError("Please confirm the photo rights agreement.");
      return;
    }
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("That email address doesn't look right.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("pet_names", petNames.trim());
    form.append("uploader_name", `${firstName.trim()} ${lastInitial.trim().charAt(0).toUpperCase()}.`);
    form.append("pet_comment", comment.trim());
    form.append("rights_confirmed", "true");
    form.append("uploader_email", email.trim());
    setStatus("uploading");
    try {
      const res = await fetch(`${API}/api/pet-photos`, { method: "POST", body: form });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Upload failed — please try again.");
      }
      setStatus("done");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-cream text-emerald">
      <Seo
        title="Share your pet's photo | Double Rainbow Suites"
        description="Share a photo of your pet enjoying their Double Rainbow Suites stay — reviewed by us before publishing."
        canonical="https://doublerainbowsuites.com/pet-photo-upload"
        noindex
      />
      <Navbar />
      <main className="pt-36 md:pt-44 pb-28 px-6">
        <div className="max-w-xl mx-auto">
          <Link to="/fort-lauderdale-pet-friendly-vacation-rentals" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-emerald-soft/60 hover:text-emerald transition-colors mb-10">
            <ArrowLeft size={13} weight="bold" /> Pet-friendly stays
          </Link>

          {status === "done" ? (
            <div data-testid="pet-upload-success" className="text-center py-16">
              <CheckCircle size={56} weight="fill" className="text-sage mx-auto mb-6" />
              <h1 className="font-display text-3xl md:text-4xl tracking-tight mb-4">Thank you!</h1>
              <p className="text-emerald-soft font-light leading-relaxed max-w-md mx-auto">
                Your photo is with us for review. Once approved, it'll join the guest gallery on our
                pet-friendly pages — we read every note from every pet.
              </p>
              <Link to="/fort-lauderdale-pet-friendly-vacation-rentals" className="btn-primary inline-flex mt-8">
                Back to pet-friendly stays
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <PawPrint size={22} weight="duotone" className="text-sunset" />
                <p className="overline text-sunset">Guest gallery</p>
              </div>
              <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-4">
                Share your pet's photo
              </h1>
              <p className="text-emerald-soft font-light leading-relaxed mb-10">
                Did your four-legged family member love their stay? Share their photo and a note in
                their voice — we review every submission before it appears on the site.
              </p>

              <form onSubmit={submit} noValidate className="space-y-7">
                <div>
                  <label className={labelCls} htmlFor="pet-photo-file">Your pet's photo</label>
                  <label
                    htmlFor="pet-photo-file"
                    className="block cursor-pointer rounded-2xl border-2 border-dashed border-emerald/20 hover:border-sunset/50 transition-colors duration-300 overflow-hidden"
                  >
                    {preview ? (
                      <img src={preview} alt="Selected pet preview" className="w-full max-h-72 object-cover" />
                    ) : (
                      <div className="py-12 text-center">
                        <Camera size={30} weight="duotone" className="text-sunset mx-auto mb-3" />
                        <p className="text-sm text-emerald font-medium">Choose a photo</p>
                        <p className="text-xs text-emerald-soft/70 mt-1">JPG, PNG, WEBP or HEIC · up to 12 MB</p>
                      </div>
                    )}
                  </label>
                  <input
                    id="pet-photo-file"
                    data-testid="pet-upload-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    className="sr-only"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </div>

                <div>
                  <label className={labelCls} htmlFor="pet-names">Pet's name (or names)</label>
                  <input id="pet-names" data-testid="pet-upload-pet-names" className={inputCls}
                    placeholder="e.g. Biscuit & gravy" value={petNames} maxLength={80}
                    onChange={(e) => setPetNames(e.target.value)} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="uploader-first">Your first name</label>
                    <input id="uploader-first" data-testid="pet-upload-first-name" className={inputCls}
                      placeholder="Alex" value={firstName} maxLength={40}
                      onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="uploader-last">Last initial</label>
                    <input id="uploader-last" data-testid="pet-upload-last-initial" className={inputCls}
                      placeholder="M" value={lastInitial} maxLength={2}
                      onChange={(e) => setLastInitial(e.target.value.replace(/[^a-zA-Z]/g, ""))} />
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="uploader-email">Your email (optional)</label>
                  <input id="uploader-email" data-testid="pet-upload-email" type="email" className={inputCls}
                    placeholder="alex@example.com" value={email} maxLength={120}
                    onChange={(e) => setEmail(e.target.value)} />
                  <p className="text-xs text-emerald-soft/60 mt-1">Only used to let you know when your photo is live — never shown on the site.</p>
                </div>

                <div>
                  <label className={labelCls} htmlFor="pet-comment">A note from your pet</label>
                  <textarea id="pet-comment" data-testid="pet-upload-comment" rows={3} className={inputCls}
                    placeholder={"e.g. \"The pool was mine all week. 10/10 would bark again.\""}
                    value={comment} maxLength={280}
                    onChange={(e) => setComment(e.target.value)} />
                  <p className="text-xs text-emerald-soft/60 mt-1">{280 - comment.length} characters left</p>
                </div>

                <div className="rounded-2xl border border-emerald/15 bg-sand/60 p-5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="pet-upload-rights-checkbox"
                      checked={rights} onChange={(e) => setRights(e.target.checked)}
                      className="mt-1 accent-sunset" />
                    <span className="text-sm text-emerald-soft font-light leading-relaxed">
                      I confirm that I own this photo or otherwise have the right to share it, and I give
                      Double Rainbow Suites unlimited permission to use this image — and the likeness of
                      anyone in it — on the Double Rainbow Suites website and in any and all marketing materials.
                    </span>
                  </label>
                </div>

                {error && (
                  <p data-testid="pet-upload-error" className="text-sm text-sunset font-medium">{error}</p>
                )}

                <button type="submit" data-testid="pet-upload-submit"
                  disabled={status === "uploading"}
                  className="btn-primary w-full justify-center disabled:opacity-60">
                  {status === "uploading" ? "Uploading…" : "Send for review"}
                </button>
                <p className="text-xs text-emerald-soft/60 text-center">
                  Photos are reviewed by the hosts before anything is published.
                </p>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
